/** Ключ session/localStorage: id платежа (БД) перед редиректом на оплату */
export const PENDING_PAYMENT_STORAGE_KEY = "virtuoz_pending_payment_id";
/** Ключ маркера «уже отправили ecommerce + цель за этот платёж» */
export const ecommerceSentKey = (paymentDbId) => `virtuoz_ecommerce_sent_${paymentDbId}`;
// Запасной дедуп в памяти вкладки — только на случай, когда НИ одно хранилище
// не приняло запись (Safari private: getItem/setItem бросают). Пишем сюда лишь
// при провале storage, поэтому в обычном режиме это пустые коллекции.
const sentInMemory = new Set();
const claimsInMemory = new Map();
/**
 * Уже отправляли покупку (цель + ecommerce) за этот платёж?
 *
 * Смотрим ОБА хранилища. sessionStorage — быстрый маркер этой вкладки;
 * localStorage — общий для всех вкладок одного origin, и он тут обязателен:
 * возврат с кассы приходит в ОТДЕЛЬНУЮ вкладку `/payment/success`, которая
 * рассылает сигнал об успехе во ВСЕ остальные вкладки приложения (см.
 * paymentReturn). Без общего маркера каждая открытая вкладка отправила бы
 * покупку ещё раз, и выручка в Метрике задвоилась бы — своей дедупликации
 * между загрузками страницы у Метрики нет.
 */
export function hasAlreadySentEcommerceForPayment(paymentDbId) {
    if (sentInMemory.has(paymentDbId)) {
        return true;
    }
    const key = ecommerceSentKey(paymentDbId);
    for (const storage of availableStorages()) {
        try {
            if (storage.getItem(key) === "1") {
                return true;
            }
        }
        catch {
            /* private mode / quota — смотрим следующее хранилище */
        }
    }
    return false;
}
/** Пометить платёж как отправленный — в оба хранилища (см. проверку выше). */
export function markEcommerceSentForPayment(paymentDbId) {
    if (!writeEverywhere(ecommerceSentKey(paymentDbId), "1")) {
        sentInMemory.add(paymentDbId);
    }
    claimsInMemory.delete(paymentDbId);
    for (const storage of availableStorages()) {
        try {
            storage.removeItem(ecommerceClaimKey(paymentDbId));
        }
        catch {
            /* ignore */
        }
    }
}
/** Ключ короткого claim «эту покупку сейчас отправляет какая-то вкладка». */
export const ecommerceClaimKey = (paymentDbId) => `virtuoz_ecommerce_claim_${paymentDbId}`;
/**
 * Сколько живёт claim. Больше, чем вкладка `/payment/success` ждёт
 * подтверждения отправки (PURCHASE_SENT_WAIT_MS в yandexMetrika), иначе вторая
 * вкладка успела бы отправить дубль, пока первая ещё ждёт tag.js.
 */
export const ECOMMERCE_CLAIM_TTL_MS = 15000;
function isFreshClaim(claimedAt, now) {
    return claimedAt !== null && now - claimedAt >= 0 && now - claimedAt < ECOMMERCE_CLAIM_TTL_MS;
}
/**
 * Занять право на отправку покупки: true — отправляем мы, false — уже
 * отправлено (постоянный маркер) либо прямо сейчас отправляет другая вкладка
 * или эта же на прошлом рендере (свежий claim).
 *
 * Claim ВРЕМЕННЫЙ: постоянный маркер ставит только подтверждение отправки
 * (`markEcommerceSentForPayment` в callback reachGoal). Раньше здесь сразу
 * ставился постоянный маркер — и если вкладка закрывалась раньше, чем tag.js
 * отправлял цель, покупка терялась навсегда. Теперь не подтвердили — claim
 * истекает, и отправит следующая попытка.
 *
 * localStorage не атомарен между вкладками: «прочитать → записать» двух
 * вкладок может переплестись. Поэтому вызывающий берёт эксклюзивный Web Lock
 * вокруг этого вызова (см. reportMetrikaPurchaseOnce); без Web Locks гонку
 * снижает случайный разнос перепроверок. Без хранилищ (Safari private) дедуп
 * деградирует до памяти этой вкладки.
 */
export function claimEcommerceSendForPayment(paymentDbId, now = Date.now()) {
    if (hasAlreadySentEcommerceForPayment(paymentDbId)) {
        return false;
    }
    if (isFreshClaim(claimsInMemory.get(paymentDbId) ?? null, now)) {
        return false;
    }
    const key = ecommerceClaimKey(paymentDbId);
    for (const storage of availableStorages()) {
        try {
            const raw = storage.getItem(key);
            const at = raw === null ? NaN : Number(raw);
            if (isFreshClaim(Number.isFinite(at) ? at : null, now)) {
                return false;
            }
        }
        catch {
            /* ignore */
        }
    }
    if (!writeEverywhere(key, String(now))) {
        claimsInMemory.set(paymentDbId, now);
    }
    return true;
}
/** Пишет во все доступные хранилища; true — хотя бы одно приняло запись. */
function writeEverywhere(key, value) {
    let written = false;
    for (const storage of availableStorages()) {
        try {
            storage.setItem(key, value);
            written = true;
        }
        catch {
            /* ignore */
        }
    }
    return written;
}
function availableStorages() {
    const out = [];
    try {
        if (typeof localStorage !== "undefined")
            out.push(localStorage);
    }
    catch {
        /* ignore */
    }
    try {
        if (typeof sessionStorage !== "undefined")
            out.push(sessionStorage);
    }
    catch {
        /* ignore */
    }
    return out;
}
